import { httpClient } from './http';
import { Category, MenuItem, MenuImportImagePayload, MenuImportProcessResult, MenuImportSessionDetail, MenuImportSessionResponse } from '../types';

const BASE_URL = '/system';

export const adminMenuService = {
  // Categorías
  async getCategoriesByRestaurant(userId: string, restaurantId: string): Promise<Category[]> {
    try {
      
      const response = await httpClient.get<Category[]>(`${BASE_URL}/users/${userId}/categories`, { restaurantId }, 'authenticated');
      
      return response;
    } catch (error) {
      console.error('❌ Error obteniendo categorías:', error);
      throw error;
    }
  },

  async createCategory(userId: string, restaurantId: string, categoryData: Partial<Category>): Promise<Category> {
    try {
      
      const payload = {
        ...categoryData,
        restaurant_id: restaurantId
      };
      const response = await httpClient.post<Category>(`${BASE_URL}/users/${userId}/categories`, payload, 'authenticated');
      
      return response;
    } catch (error) {
      console.error('❌ Error creando categoría:', error);
      throw error;
    }
  },

  async updateCategory(userId: string, restaurantId: string, categoryId: string, categoryData: Partial<Category>): Promise<Category> {
    try {
      
      const payload = {
        ...categoryData,
        restaurant_id: restaurantId
      };
      const response = await httpClient.put<Category>(`${BASE_URL}/users/${userId}/categories/${categoryId}`, payload, 'authenticated');
      
      return response;
    } catch (error) {
      console.error('❌ Error actualizando categoría:', error);
      throw error;
    }
  },

  async deleteCategory(userId: string, restaurantId: string, categoryId: string): Promise<void> {
    try {
      
      await httpClient.delete(`${BASE_URL}/users/${userId}/restaurants/${restaurantId}/categories/${categoryId}`, 'authenticated');
      
    } catch (error) {
      console.error('❌ Error eliminando categoría:', error);
      throw error;
    }
  },

  // Items del menú
  async getMenuItemsByRestaurant(userId: string, restaurantId: string): Promise<MenuItem[]> {
    try {
      
      const response = await httpClient.get<MenuItem[]>(`${BASE_URL}/users/${userId}/restaurants/${restaurantId}/menu`, {}, 'authenticated');
      
      
      
      
      
      // El backend devuelve un objeto con { data: [...], pagination: {...} }
      if (response && typeof response === 'object' && !Array.isArray(response) && 'data' in response) {
        
        return (response as any).data || [];
      }
      
      // Si es un array, devolverlo directamente
      if (Array.isArray(response)) {
        return response;
      }
      
      // Si es un objeto pero no tiene la estructura esperada, devolver array vacío
      console.warn('⚠️ Respuesta inesperada del backend:', response);
      return [];
    } catch (error) {
      console.error('❌ Error obteniendo items del menú:', error);
      throw error;
    }
  },

  async createMenuItem(userId: string, restaurantId: string, menuItemData: Partial<MenuItem>): Promise<MenuItem> {
    try {
      
      const response = await httpClient.post<MenuItem>(`${BASE_URL}/users/${userId}/restaurants/${restaurantId}/menu`, menuItemData, 'authenticated');
      
      return response;
    } catch (error) {
      console.error('❌ Error creando item del menú:', error);
      throw error;
    }
  },

  async updateMenuItem(userId: string, restaurantId: string, menuItemId: string, menuItemData: Partial<MenuItem>): Promise<MenuItem> {
    try {
      
      const response = await httpClient.put<MenuItem>(`${BASE_URL}/users/${userId}/restaurants/${restaurantId}/menu/${menuItemId}`, menuItemData, 'authenticated');
      
      return response;
    } catch (error) {
      console.error('❌ Error actualizando item del menú:', error);
      throw error;
    }
  },

  async deleteMenuItem(userId: string, restaurantId: string, menuItemId: string): Promise<void> {
    try {
      
      await httpClient.delete(`${BASE_URL}/users/${userId}/restaurants/${restaurantId}/menu/${menuItemId}`, 'authenticated');
      
    } catch (error) {
      console.error('❌ Error eliminando item del menú:', error);
      throw error;
    }
  },

  async updateMenuItemAvailability(userId: string, restaurantId: string, menuItemId: string, available: boolean): Promise<MenuItem> {
    try {
      
      const response = await httpClient.patch<MenuItem>(`${BASE_URL}/users/${userId}/restaurants/${restaurantId}/menu/${menuItemId}/availability`, { available }, 'authenticated');
      
      return response;
    } catch (error) {
      console.error('❌ Error actualizando disponibilidad:', error);
      throw error;
    }
  },

  // Importar menú desde imágenes
  async createMenuImportSession(
    userId: string,
    restaurantId: string,
    images: MenuImportImagePayload[]
  ): Promise<MenuImportSessionResponse> {
    try {
      const response = await httpClient.post<MenuImportSessionResponse>(
        `${BASE_URL}/users/${userId}/restaurants/${restaurantId}/menu/import-from-images/session`,
        { images },
        'authenticated'
      );

      return response;
    } catch (error) {
      console.error('❌ Error creando sesión de importación:', error);
      throw error;
    }
  },

  async processMenuImportSession(
    userId: string,
    restaurantId: string,
    sessionId: string
  ): Promise<MenuImportProcessResult> {
    try {
      const response = await httpClient.post<MenuImportProcessResult>(
        `${BASE_URL}/users/${userId}/restaurants/${restaurantId}/menu/import-from-images/process`,
        { session_id: sessionId },
        'authenticated'
      );

      return response;
    } catch (error) {
      console.error('❌ Error procesando importación:', error);
      throw error;
    }
  },

  async getMenuImportSession(
    userId: string,
    restaurantId: string,
    sessionId: string
  ): Promise<MenuImportSessionDetail> {
    try {
      const response = await httpClient.get<MenuImportSessionDetail>(
        `${BASE_URL}/users/${userId}/restaurants/${restaurantId}/menu/import-from-images/session/${sessionId}`,
        undefined,
        'authenticated'
      );

      return response;
    } catch (error) {
      console.error('❌ Error obteniendo sesión de importación:', error);
      throw error;
    }
  },

  async deleteMenuImportImage(
    userId: string,
    restaurantId: string,
    sessionId: string,
    imageId: string
  ): Promise<MenuImportSessionDetail> {
    try {
      const response = await httpClient.delete<MenuImportSessionDetail>(
        `${BASE_URL}/users/${userId}/restaurants/${restaurantId}/menu/import-from-images/session/${sessionId}/images/${imageId}`,
        'authenticated'
      );

      return response;
    } catch (error) {
      console.error('❌ Error eliminando imagen de importación:', error);
      throw error;
    }
  },

  // Manejo de imágenes
  async uploadMenuItemImages(
    userId: string, 
    restaurantId: string, 
    menuItemId: string, 
    images: File[], 
    onProgress?: (current: number, total: number) => void
  ): Promise<string[]> {
    try {
      console.log('📤 Iniciando upload de imágenes:', { userId, restaurantId, menuItemId, imageCount: images.length });
      
      // Extraer nombres de archivos de las imágenes
      const imageNames = images.map(img => img.name);
      console.log('📋 Nombres de archivos:', imageNames);
      
      // Obtener URLs firmadas del backend
      const signedUrlsResponse = await httpClient.post<Array<{ id: string, signedUrl: string, altText: string }>>(
        `${BASE_URL}/users/${userId}/restaurants/${restaurantId}/menu/${menuItemId}/images`,
        { images: imageNames },
        'authenticated'
      );
      
      console.log('🔍 Respuesta del backend:', signedUrlsResponse);
      
      // Validar que la respuesta sea un array
      if (!signedUrlsResponse || !Array.isArray(signedUrlsResponse)) {
        console.error('❌ Respuesta inesperada del backend:', signedUrlsResponse);
        throw new Error('Respuesta del backend no es un array');
      }
      
      // Subir cada imagen usando las URLs firmadas
      const uploadedIds: string[] = [];
      const totalImages = signedUrlsResponse.length;
      
      for (let i = 0; i < signedUrlsResponse.length; i++) {
        const imgMeta = signedUrlsResponse[i];
        const imgFile = images.find(img => img.name === imgMeta.altText);
        
        if (!imgFile) {
          console.warn('⚠️ No se encontró archivo para:', imgMeta.altText);
          continue;
        }
        
        // Reportar progreso antes de subir
        if (onProgress) {
          onProgress(i, totalImages);
        }
        
        // Subir imagen directamente a la URL firmada
        const uploadResponse = await fetch(imgMeta.signedUrl, {
          method: 'PUT',
          body: imgFile,
          headers: {
            'Content-Type': imgFile.type,
          },
        });
        
        if (!uploadResponse.ok) {
          throw new Error(`Error subiendo imagen ${imgFile.name}: ${uploadResponse.statusText}`);
        }
        
        uploadedIds.push(imgMeta.id);
        
        // Reportar progreso después de subir exitosamente
        if (onProgress) {
          onProgress(i + 1, totalImages);
        }
      }
      
      return uploadedIds;
    } catch (error) {
      console.error('❌ Error subiendo imágenes:', error);
      throw error;
    }
  },

  async deleteMenuItemImage(userId: string, restaurantId: string, menuItemId: string, imageId: string): Promise<void> {
    try {
      
      await httpClient.delete(`${BASE_URL}/users/${userId}/restaurants/${restaurantId}/menu/${menuItemId}/images/${imageId}`, 'authenticated');
      
    } catch (error) {
      console.error('❌ Error eliminando imagen:', error);
      throw error;
    }
  },

  async reorderMenuItemImages(userId: string, restaurantId: string, menuItemId: string, imageIds: string[]): Promise<void> {
    try {
      // Construir el payload correcto con id y display_order
      const images = imageIds.map((id, idx) => ({ id, display_order: idx + 1 }));
      await httpClient.post(`${BASE_URL}/users/${userId}/restaurants/${restaurantId}/menu/${menuItemId}/images/reorder`, { images }, 'authenticated');
    } catch (error) {
      console.error('❌ Error reordenando imágenes:', error);
      throw error;
    }
  },

  // Obtener imágenes de un platillo
  async getMenuItemImages(userId: string, restaurantId: string, menuItemId: string): Promise<any[]> {
    try {
      
      const response = await httpClient.get<any[]>(`${BASE_URL}/users/${userId}/restaurants/${restaurantId}/menu/${menuItemId}/images`, {}, 'authenticated');
      
      return response || [];
    } catch (error) {
      console.error('❌ Error obteniendo imágenes del platillo:', error);
      throw error;
    }
  }
}; 